import type { PasswordInputStrength } from '@ksyos/design-system';
import {
    Box,
    Button,
    ButtonRow,
    Form,
    FormButtonRow,
    FormGroup,
    Heading,
    PasswordInput,
    SuccessAnimation,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import type { IPractitioner } from '../../interfaces/practitioner';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { MINIMUM_PASSWORD_LENGTH } from '../../../../common/practitionerDomain';
import { fetchJson } from '../../../store/fetch';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../customControls/utils/formAnalytics';

export interface IActivationPractitioner extends IPractitioner {
    userName: string;
}

export interface ISetPasswordProps {
    activationToken: string;
    goBack?: () => void;
    goToNextStep?: (newPassword: string) => void;
    practitionerDisplayName?: string;
    redirectPath: string;
    showSuccessScreen?: boolean;
    title: string;
    userName?: string;
}

export function SetPassword(props: ISetPasswordProps) {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const [passwordError, setPasswordError] = useState<ValidationErrorCode | undefined>();
    const [password, setPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState<PasswordInputStrength>(0);
    const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
    const [passwordIsValid, setPasswordIsValid] = useState<boolean>(false);
    const [isValidatingPassword, setIsValidatingPassword] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        addFormAnalytics(formRef.current, buttonRef.current);
    }, []);

    const debouncedValidatePassword = useCallback(debounce(validatePassword, 200), []);

    async function validatePassword(newPassword: string) {
        setIsValidatingPassword(true);
        let isPasswordValid = false;

        if (!newPassword) {
            setPasswordError(ValidationErrorCode.NO_PASSWORD);
            setPasswordStrength(0);
        } else if (newPassword.length < MINIMUM_PASSWORD_LENGTH / 2) {
            setPasswordError(ValidationErrorCode.VERY_SHORT_PASSWORD);
            setPasswordStrength(1);
        } else if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
            setPasswordError(ValidationErrorCode.SHORT_PASSWORD);
            setPasswordStrength(2);
        } else {
            const result = await fetchJson(
                'api/validatePassword/',
                'POST',
                { activationToken: props.activationToken, password: newPassword },
                [
                    ValidationErrorCode.BAD_QUALITY_PASSWORD,
                    ValidationErrorCode.PASSWORD_CONTAINS_FREQUENTLY_USED_PASSWORD,
                    ValidationErrorCode.PASSWORD_CONTAINS_PERSONAL_INFORMATION,
                    ValidationErrorCode.PASSWORD_CONTAINS_REPEATED_SUBSEQUENCE,
                ]
            );

            if (result.errorCode) {
                setPasswordError(result.errorCode as ValidationErrorCode);
                setPasswordStrength(3);
            } else if (result.status === 200) {
                setPasswordError(undefined);
                isPasswordValid = true;
                setPasswordStrength(result.json.strength + 1);
            }
        }

        setPasswordIsValid(isPasswordValid);
        setIsValidatingPassword(false);
    }

    async function sendPassword() {
        addTrackFormSubmit(buttonRef.current);
        const response = await fetchJson('api/createPassword/', 'POST', {
            activationToken: props.activationToken,
            password,
        });
        if (response.status === 200) {
            addFormConversion(formRef.current);
        }
        if (props.showSuccessScreen) {
            setShowSuccessScreen(true);
        } else {
            if (props.goToNextStep) {
                props.goToNextStep(password);
            } else {
                navigate(props.redirectPath);
            }
        }
    }

    return (
        <>
            {!showSuccessScreen && (
                <>
                    <Heading>{props.title}</Heading>
                    <Text>
                        Welkom, <b>{props.practitionerDisplayName ?? ''}</b>.
                        <br />
                        <br />
                        Kies een wachtwoord van minimaal {MINIMUM_PASSWORD_LENGTH} karakters om verder te gaan.
                    </Text>
                    <Form
                        formRef={formRef}
                        id="SetPassword"
                        name="SetPassword"
                        submitOnEnter={true}
                    >
                        <FormGroup
                            label="Gebruikersnaam"
                            layout="vertical"
                        >
                            <TextInput
                                disabled
                                name="userName"
                                type="text"
                                value={props.userName ?? ''}
                            />
                        </FormGroup>
                        <FormGroup
                            label="Wachtwoord"
                            layout="vertical"
                        >
                            <PasswordInput
                                capsLockText="Capslock staat aan"
                                error={passwordError ? translateKey(`login.error.${passwordError}`) : ''}
                                name="password"
                                onChange={async (e) => {
                                    setPassword(e.target.value);
                                    await debouncedValidatePassword(e.target.value);
                                }}
                                strength={passwordStrength}
                                value={password}
                            />
                        </FormGroup>
                        <FormButtonRow>
                            {passwordIsValid && (
                                <Button
                                    buttonRef={buttonRef}
                                    className="fade-in"
                                    color="secondary"
                                    onClick={async () => {
                                        if (!isValidatingPassword) {
                                            await sendPassword();
                                        }
                                    }}
                                    width="full"
                                >
                                    {translate.common.next}
                                </Button>
                            )}
                            {props.goBack && (
                                <Button
                                    color="secondary"
                                    key="prev"
                                    onClick={() => props.goBack && props.goBack()}
                                    variant="outline"
                                    width="full"
                                >
                                    {translate.common.previous}
                                </Button>
                            )}
                        </FormButtonRow>
                    </Form>
                </>
            )}
            {showSuccessScreen && (
                <>
                    <Heading>Wachtwoord vergeten</Heading>
                    <Box
                        alignItems="center"
                        display="flex"
                        flexDirection="column"
                    >
                        <SuccessAnimation size="large" />
                    </Box>
                    <Text textAlign="center">Gelukt! Je nieuwe wachtwoord is ingesteld</Text>
                    <ButtonRow>
                        <Button
                            color="secondary"
                            onClick={() => {
                                navigate(props.redirectPath);
                            }}
                            width="full"
                        >
                            Inloggen
                        </Button>
                    </ButtonRow>
                </>
            )}
        </>
    );
}
